// React hook for managing job result import functionality

import { useState, useEffect, useCallback } from 'react';
import { listJobs, type JobWithResult } from '../lib/jobs';
import { parseMolecularFile } from '../../molecules/lib/molecularParser';
import type {
  ImportableJob,
  MolecularOutputType,
  ParsingLevel,
  ParsedMolecularData,
  ParsingOptions,
  MolecularParsingError,
  ParsingProgress
} from '../../molecules/types/molecular';

interface UseJobImportOptions {
  projectId?: string;
  autoLoadJobs?: boolean;
}

interface UseJobImportReturn {
  // Job data
  jobs: ImportableJob[];
  selectedJob: ImportableJob | null;
  
  // Parsing data
  parsedData: ParsedMolecularData | null;
  previewData: ParsedMolecularData | null;
  
  // State management
  loading: boolean;
  parsing: boolean;
  error: MolecularParsingError | null;
  progress: ParsingProgress | null;
  
  // Actions
  loadJobs: (projectId: string) => Promise<void>;
  selectJob: (job: ImportableJob) => void;
  parseJobOutput: (
    outputType: MolecularOutputType,
    level: ParsingLevel,
    options?: ParsingOptions
  ) => Promise<void>;
  clearParsedData: () => void;
  clearError: () => void;
}

/**
 * Custom hook for managing job result imports
 */
export function useJobImport({
  projectId,
  autoLoadJobs = true
}: UseJobImportOptions = {}): UseJobImportReturn {
  
  // State management
  const [jobs, setJobs] = useState<ImportableJob[]>([]);
  const [selectedJob, setSelectedJob] = useState<ImportableJob | null>(null);
  const [parsedData, setParsedData] = useState<ParsedMolecularData | null>(null);
  const [previewData, setPreviewData] = useState<ParsedMolecularData | null>(null);
  const [loading, setLoading] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [error, setError] = useState<MolecularParsingError | null>(null);
  const [progress, setProgress] = useState<ParsingProgress | null>(null);

  /**
   * Load and filter jobs for import
   */
  const loadJobs = useCallback(async (targetProjectId: string) => {
    setLoading(true);
    setError(null);
    
    try {
      // Fetch all jobs for the user
      const allJobs = await listJobs();
      
      // Filter for completed jobs in the specified project
      // Handle cases where jobs might not have project_id or might be null
      const projectJobs = allJobs.filter(job => {
        const isCompleted = job.status === 'completed';
        const hasResults = job.result && (
          job.result.rfdiffusion_pdb_url || 
          job.result.active_site_cif_url || 
          job.result.neighbors_json_url
        );
        
        // If targetProjectId is provided, filter by project
        // If job.project_id is null/undefined, include it if no specific project filter
        const matchesProject = targetProjectId ? 
          (job.project_id === targetProjectId || job.project_id === null) : 
          true;
        
        
        return isCompleted && hasResults && matchesProject;
      });
      
      // Transform to ImportableJob format
      const importableJobs: ImportableJob[] = projectJobs.map(job => {
        const result = job.result!;
        
        return {
          id: job.id,
          name: job.name,
          smiles: job.smiles,
          status: job.status,
          created_at: job.created_at,
          project_id: job.project_id,
          availableOutputs: {
            proteinDesign: !!result.rfdiffusion_pdb_url,
            activeSite: !!result.active_site_cif_url,
            neighbors: !!result.neighbors_json_url
          },
          resultUrls: {
            proteinDesign: result.rfdiffusion_pdb_url || undefined,
            activeSite: result.active_site_cif_url || undefined,
            neighbors: result.neighbors_json_url || undefined
          }
        };
      });
      
      // Sort by creation date (newest first)
      importableJobs.sort((a, b) => 
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
      
      setJobs(importableJobs);
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error loading jobs';
      setError({
        type: 'network',
        message: `Failed to load jobs: ${errorMessage}`,
        recoverable: true
      });
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Select a job for import
   */
  const selectJob = useCallback((job: ImportableJob) => {
    setSelectedJob(job);
    clearParsedData();
  }, []);

  /**
   * Parse molecular file from job output
   */
  const parseJobOutput = useCallback(async (
    outputType: MolecularOutputType,
    level: ParsingLevel,
    options: ParsingOptions = { level }
  ) => {
    if (!selectedJob) {
      setError({
        type: 'parsing',
        message: 'No job selected for import',
        recoverable: true
      });
      return;
    }

    const fileUrl = selectedJob.resultUrls[outputType];
    if (!fileUrl) {
      setError({
        type: 'parsing',
        message: `Selected job does not have ${outputType} output available`,
        recoverable: true
      });
      return;
    }

    setParsing(true);
    setError(null);
    setParsedData(null);
    setPreviewData(null);

    try {
      // Download progress
      setProgress({
        stage: 'downloading',
        progress: 0,
        currentItem: 'Downloading molecular file...'
      });

      // Fetch the molecular file
      const response = await fetch(fileUrl);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      // Check file size
      const contentLength = response.headers.get('content-length');
      const fileSize = contentLength ? parseInt(contentLength) : 0;
      
      if (fileSize > 50 * 1024 * 1024) { // 50MB limit
        throw new Error('File too large (>50MB). Please contact support for assistance.');
      }

      setProgress({
        stage: 'downloading',
        progress: 50,
        currentItem: 'Processing file content...'
      });

      const content = await response.text();

      if (!content || content.trim().length === 0) {
        throw new Error('File appears to be empty or corrupted');
      }

      // Parsing progress
      setProgress({
        stage: 'parsing',
        progress: 75,
        currentItem: `Parsing to ${level} level...`
      });

      // Parse the molecular data
      const parsedResult = parseMolecularFile(content, level, {
        ...options,
        maxAtoms: options.maxAtoms || 10000 // Default limit for performance
      });

      // Add file metadata
      parsedResult.metadata = {
        ...parsedResult.metadata,
        fileName: fileUrl.split('/').pop() || 'molecular_data',
        fileSize
      };

      // Final processing
      setProgress({
        stage: 'processing',
        progress: 90,
        currentItem: 'Finalizing data...'
      });

      // Create preview data (first 10 rows)
      const preview: ParsedMolecularData = {
        ...parsedResult,
        rows: parsedResult.rows.slice(0, 10)
      };

      setParsedData(parsedResult);
      setPreviewData(preview);

      setProgress({
        stage: 'complete',
        progress: 100,
        currentItem: 'Import ready'
      });

    } catch (err) {
      let errorType: MolecularParsingError['type'] = 'parsing';
      let errorMessage = 'Unknown error occurred';
      let recoverable = true;

      if (err instanceof Error) {
        errorMessage = err.message;
        
        // Categorize error types
        if (err.message.includes('HTTP')) {
          errorType = 'network';
        } else if (err.message.includes('large') || err.message.includes('memory')) {
          errorType = 'fileSize';
          recoverable = false;
        } else if (err.message.includes('format') || err.message.includes('parse')) {
          errorType = 'fileFormat';
        }
      }

      setError({
        type: errorType,
        message: errorMessage,
        recoverable
      });

    } finally {
      setParsing(false);
      setProgress(null);
    }
  }, [selectedJob]);

  /**
   * Clear parsed data
   */
  const clearParsedData = useCallback(() => {
    setParsedData(null);
    setPreviewData(null);
    setProgress(null);
  }, []);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Auto-load jobs when projectId changes
   */
  useEffect(() => {
    if (autoLoadJobs && projectId) {
      loadJobs(projectId);
    }
  }, [projectId, autoLoadJobs, loadJobs]);

  return {
    // Data
    jobs,
    selectedJob,
    parsedData,
    previewData,
    
    // State
    loading,
    parsing,
    error,
    progress,
    
    // Actions
    loadJobs,
    selectJob,
    parseJobOutput,
    clearParsedData,
    clearError
  };
}

/**
 * Helper hook for getting import suggestions based on output type
 */
export function useImportSuggestions(outputType: MolecularOutputType | null) {
  const getSuggestedLevel = useCallback((type: MolecularOutputType): ParsingLevel => {
    switch (type) {
      case 'proteinDesign':
        return 'residue'; // Best for analyzing designed proteins
      case 'activeSite':
        return 'binding'; // Focus on binding interactions
      case 'neighbors':
        return 'chain'; // Overview of neighboring regions
      default:
        return 'atomic';
    }
  }, []);

  const getSuggestedOptions = useCallback((type: MolecularOutputType): ParsingOptions => {
    const level = getSuggestedLevel(type);
    
    const baseOptions: ParsingOptions = {
      level,
      includeHydrogens: false,
      precision: 3,
      maxAtoms: 10000
    };

    switch (type) {
      case 'activeSite':
        return {
          ...baseOptions,
          precision: 2, // Less precision needed for binding analysis
          maxAtoms: 5000 // Active sites are typically smaller
        };
      case 'proteinDesign':
        return {
          ...baseOptions,
          precision: 3 // Good precision for structural analysis
        };
      case 'neighbors':
        return {
          ...baseOptions,
          precision: 2 // Overview level, less precision needed
        };
      default:
        return baseOptions;
    }
  }, [getSuggestedLevel]);

  return {
    getSuggestedLevel,
    getSuggestedOptions
  };
}